import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import { useGetBagStatus, useUpdateBagSettings } from '../../hooks/backend/bagsPage'
import { notifyBagStatusChanged, type BagSize, type BagStatus } from '../../utils/bagStatus'
import { HelpText } from '../../components/HelpText'

interface Draft {
  lead: string
  point: string
}

export default function BagReorderSettings() {
  const getBagStatus = useGetBagStatus()
  const updateBagSettings = useUpdateBagSettings()
  const status = getBagStatus.data as BagStatus | null | undefined

  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [busy, setBusy] = useState<BagSize | null>(null)

  useEffect(() => {
    void getBagStatus.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!status) return
    const next: Record<string, Draft> = {}
    for (const s of status.sizes) {
      next[s.size] = {
        lead: String(s.lead_time_days),
        point: s.reorder_point_override === null ? '' : String(s.reorder_point_override),
      }
    }
    setDrafts(next)
  }, [status])

  const save = async (size: BagSize) => {
    const draft = drafts[size]
    if (!draft) return
    const lead = Number(draft.lead)
    if (!Number.isInteger(lead) || lead < 1) {
      toast.error('Lead time must be at least 1 day')
      return
    }
    const point = draft.point.trim() === '' ? null : Number(draft.point)
    if (point !== null && (!Number.isInteger(point) || point < 0)) {
      toast.error('Reorder point must be a whole number of bags')
      return
    }
    try {
      setBusy(size)
      await updateBagSettings.trigger({ size, lead_time_days: lead, reorder_point: point })
      await getBagStatus.trigger({}, { skipCache: true })
      notifyBagStatusChanged()
      toast.success(`${size} bag settings saved`)
    } catch {
      toast.error('Failed to save bag settings')
    } finally {
      setBusy(null)
    }
  }

  const setDraft = (size: BagSize, patch: Partial<Draft>) =>
    setDrafts(d => ({ ...d, [size]: { ...(d[size] ?? { lead: '14', point: '' }), ...patch } }))

  return (
    <Card id="bag-reorder">
      <CardHeader>
        <CardTitle>Bag Reorder</CardTitle>
        <CardDescription><HelpText>
          Lead time is how many days new bags take to arrive. The suggested reorder point is the
          average bags used per day over the last 30 days, times the lead time, plus a 25% buffer.
          Enter your own reorder point to override it, or leave it blank to use the suggestion.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent>
        {getBagStatus.error && !status ? (
          <p className="text-sm text-destructive">Couldn't load bag settings.</p>
        ) : !status ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="divide-y rounded-md border">
            {status.sizes.map(s => {
              const draft = drafts[s.size] ?? { lead: '', point: '' }
              return (
                <div
                  key={s.size}
                  className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-[6rem,8rem,1fr,auto] sm:items-end"
                >
                  <span className="font-medium sm:pb-2">{s.size}</span>
                  <div className="space-y-1.5">
                    <Label htmlFor={`bag-lead-${s.size}`}>Lead time (days)</Label>
                    <Input
                      id={`bag-lead-${s.size}`}
                      type="number"
                      min="1"
                      step="1"
                      value={draft.lead}
                      onChange={e => setDraft(s.size, { lead: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`bag-point-${s.size}`}>Reorder point (bags)</Label>
                    <Input
                      id={`bag-point-${s.size}`}
                      type="number"
                      min="0"
                      step="1"
                      value={draft.point}
                      onChange={e => setDraft(s.size, { point: e.target.value })}
                      placeholder={`Suggested: ${s.suggested_reorder_point.toLocaleString('en-US')}`}
                      className="h-9"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => void save(s.size)}
                    disabled={busy === s.size}
                    className="gap-1.5 shrink-0"
                  >
                    {busy === s.size && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
